<template>
    <div :id="wrapperId" class="mapbox__wrapper">
        <div :id="instanceId" ref="mapbox" class="mapbox__map"></div>

        <div v-if="hasError" class="mapbox__error">
            {{ $t('errors.map_not_loadable', { i18nFallback: 'Map could not be loaded.' }) }}
        </div>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { vrmConfig } from '@/config';
import ResetZoomControl from '@/vue/components/map/controls/reset_zoom_control';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { Component, Vue } from 'vue-property-decorator';

mapboxgl.accessToken = vrmConfig.mapboxApiKey;

/**
 * Basic Mapbox component for loading and configuring a basic map.
 *
 * The "load" event can be used to gain access to the Mapbox API object.
 */
@Component({
    name: 'VrmMapbox',
    props: {
        /**
         * Desired map projection.
         *
         * Defaults to mercator (flat map)
         *
         * See: https://docs.mapbox.com/mapbox-gl-js/guides/projections/#what-projections-are-available
         */
        projection: {
            type: String,
            required: false,
            default: 'mercator',
        },
        /**
         * Mapbox style url for the map.
         */
        mapStyle: {
            type: String,
            required: false,
            default: 'mapbox://styles/elninotech/clu86ujd4003e01pe4lxh0gts',
        },
        /**
         * Location model for the map.
         *
         * Object containing lat, lng, zoom, bearing and pitch.
         */
        location: {
            type: Object,
            required: true,
        },
        /**
         * Should an indication of the map scale be shown?
         *
         * Defaults to true.
         */
        scaleControls: {
            type: Boolean,
            required: false,
            default: true,
        },
        /**
         * Should navigation controls (zoom/compass) be shown?
         *
         * Defaults to true.
         */
        navigationControls: {
            type: Boolean,
            required: false,
            default: true,
        },
        /**
         * Should geolocate controls be shown?
         *
         * Defaults to true.
         */
        geolocateControls: {
            type: Boolean,
            required: false,
            default: true,
        },
        /**
         * Should fullscreen controls be shown?
         *
         * Defaults to true.
         */
        fullscreenControls: {
            type: Boolean,
            required: false,
            default: true,
        },
        /**
         * Should reset zoom controls be shown?
         *
         * Defaults to true.
         */
        resetZoomControls: {
            type: Boolean,
            required: false,
            default: true,
        },
        /**
         * Target identifier for the fullscreen control.
         *
         * Only used if fullscreenControls is true.
         */
        fullscreenTarget: {
            type: String,
            required: false,
            default: null,
        },
    },
    emits: [
        'load',
        'move',
        'zoom',
        'rotate',
        'pitch',
        'render',
        'location',
        'bounds',
        'boundsend',
        'movestart',
        'moveend',
        'error',
    ],
    data() {
        return {
            wrapperId: null,
            instanceId: null,
            errorCode: null,
            errorMessage: null,
        };
    },
    created() {
        this.map = null;
        this.instanceId = `mapbox-${this._uid}`;
        this.wrapperId = `${this.instanceId}__wrapper`;
    },
    mounted: function() {
        const { lng, lat, zoom, bearing, pitch } = this.location;

        // Initialize mapbox
        this.map = new mapboxgl.Map({
            container: this.instanceId,
            style: this.mapStyle,
            center: [lng, lat],
            zoom: zoom,
            cooperativeGestures: true,
            antialias: true, // create the gl context with MSAA antialiasing, so custom layers are antialiased
            attributionControl: false,
            touchPitch: false,
            pitchWithRotate: false,
            pitch: pitch,
            bearing: bearing,
            fadeDuration: 0,
            projection: this.projection,
        });

        // Add map controls based on the set properties.
        this.initializeControls();

        this.map.on('load', () => {
            this.map.resize();

            this.$emit('load', this.map);

            this.attachEventListeners();
        });

        this.map.on('error', e => {
            this.$emit('error', e);

            if (!e.sourceId && !e.source) {
                console.error('Mapbox Error: ', e);

                this.errorCode = e.error.status;
                this.errorMessage = e.error.message;
            }
        });
    },
    beforeDestroy() {
        this.map.remove();
        this.map = null;
    },
    watch: {
        location(next) {
            const curr = this.getLocation();
            const map = this.map;

            if (curr.lng !== next.lng || curr.lat !== next.lat) {
                map.setCenter({ lng: next.lng, lat: next.lat });
            }

            if (curr.pitch !== next.pitch) map.setPitch(next.pitch);
            if (curr.bearing !== next.bearing) map.setBearing(next.bearing);
            if (curr.zoom !== next.zoom) map.setZoom(next.zoom);
        },
    },
    methods: {
        /**
         * Called whenever the map container changes dimensions.
         *
         * Resize mapbox.
         */
        onResize() {
            this.map.resize();
        },
        /**
         * Get the current location object of the map including zoom, bearing and pitch.
         *
         * @returns {{lng: *, bearing: (number|*), zoom: *, pitch: (number|*), lat: *}}
         */
        getLocation() {
            const { lng, lat } = this.map.getCenter();

            return {
                lat: lat,
                lng: lng,
                zoom: this.map.getZoom(),
                bearing: this.map.getBearing(),
                pitch: this.map.getPitch(),
            };
        },
        /**
         * Initialize the map controls and add them to the map object.
         */
        initializeControls() {
            if (this.fullscreenControls) {
                this.map.addControl(
                    new mapboxgl.FullscreenControl({
                        container: document.querySelector(
                            this.fullscreenTarget || `#${this.instanceId}`,
                        ),
                    }),
                );
            }

            if (this.navigationControls) {
                this.map.addControl(new mapboxgl.NavigationControl());
            }

            if (this.resetZoomControls) {
                const resetZoomControl = new ResetZoomControl();
                this.map.addControl(resetZoomControl);
            }

            if (this.geolocateControls) {
                this.map.addControl(new mapboxgl.GeolocateControl());
            }

            if (this.scaleControls) {
                this.map.addControl(new mapboxgl.ScaleControl(), 'bottom-right');
            }
        },
        /**
         * Attach event listeners to the map and emit events the Vue way.
         */
        attachEventListeners() {
            const updateLocation = () => {
                this.$emit('location', this.getLocation());
                this.$emit('bounds', this.map.getBounds());
            };

            this.map.on('movestart', () => {
                this.$emit('movestart', this.map.getCenter());
            });

            this.map.on('move', () => {
                this.$emit('bounds', this.map.getBounds());
            });

            this.map.on('moveend', () => {
                this.$emit('moveend', this.map.getCenter());
                this.$emit('boundsend', this.map.getBounds());

                updateLocation();
            });

            this.map.on('zoomend', () => {
                this.$emit('zoom', this.map.getZoom());
                this.$emit('boundsend', this.map.getBounds());

                updateLocation();
            });

            this.map.on('rotateend', () => {
                this.$emit('rotate', this.map.getBearing());
                this.$emit('boundsend', this.map.getBounds());

                updateLocation();
            });

            this.map.on('pitchend', () => {
                this.$emit('pitch', this.map.getPitch());
                this.$emit('boundsend', this.map.getBounds());

                updateLocation();
            });

            // When the map is rendered, update the tank/service stat popups.
            this.map.on('render', () => {
                this.$emit('render', this.map);
            });
        },
    },
    computed: {
        /**
         * Checks if an error has occured.
         *
         * @returns {*}
         */
        hasError() {
            return this.errorCode && this.errorMessage;
        },
    },
})
@AngularLocaleBundle('errors')
export default class VrmMapbox extends Vue {}
</script>
